import { useMutation } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import API from '@/api';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { useOpenPaymentProvider } from '@/components/PaymentProvider/PaymentProviderProvider';
import { showErrorToast } from '@/components/Toast';
import { ProductNames } from '@/constants/ProductNames';
import useModal from '@/hooks/useModal/useModal';
import { routeMap } from '@/routes/routeMap';
import Button from '@/ui-kit/components/Button/Button';
import { DataList, DataListItem, DataListLabel, DataListValue } from '@/ui-kit/components/DataList/DataList';
import { constructURL } from '@/utils/url';

type Payload = { loanIdProtectDebt: number };
type Props = ModalProps<Payload>;

function RemainingDebtInfoLoanIdProtectInGracePeriod({ payload: { loanIdProtectDebt } }: Props) {
  const { t } = useTranslation(['Modal', 'Common', 'ErrorToast']);
  const openPaymentProvider = useOpenPaymentProvider();
  const { closeModal } = useModal();

  const { mutate: payExtra, isPending: isPayExtraPending } = useMutation({
    mutationFn: () =>
      API.paymentProvider.payExtra({
        productName: ProductNames.LOAN,
        amount: loanIdProtectDebt,
        successfulUrl: constructURL(routeMap.userProfile.SE.loanIdProtectTerminationInGracePeriod.success),
        failedUrl: constructURL(routeMap.userProfile.SE.loanIdProtectTerminationInGracePeriod.failure),
      }),
    onSuccess: paymentProviderUrl => {
      closeModal();
      openPaymentProvider(paymentProviderUrl);
    },
    onError: () => {
      showErrorToast(t('ErrorToast:defaultMessage'));
    },
  });

  return (
    <Modal title={t('RemainingDebtInfoLoanIdProtectInGracePeriod.title')}>
      <div className="flex flex-col gap-4">
        <p>{t('RemainingDebtInfoLoanIdProtectInGracePeriod.info1')}</p>
        <p>{t('RemainingDebtInfoLoanIdProtectInGracePeriod.info2')}</p>

        <DataList>
          <DataListItem>
            <DataListLabel>
              {t('RemainingDebtInfoLoanIdProtectInGracePeriod.loanIdProtectProduct')}
            </DataListLabel>
            <DataListValue>{t('Common:money', { amount: loanIdProtectDebt })}</DataListValue>
          </DataListItem>
        </DataList>
      </div>
      <ModalActions>
        <Button variant="destructive" onClick={() => payExtra()} disabled={isPayExtraPending}>
          {t('RemainingDebtInfoLoanIdProtectInGracePeriod.button')}
        </Button>
      </ModalActions>
    </Modal>
  );
}

export default RemainingDebtInfoLoanIdProtectInGracePeriod;
